import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, input, model, output, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { form, required, validate } from '@angular/forms/signals';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { FloatLabelModule } from 'primeng/floatlabel';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { firstValueFrom } from 'rxjs';

import { RejectionReason } from '@core/models/rejection-reason';
import { MenuItemsService } from '@core/services/menu-items';

interface RejectReasonFormModel {
  reasonCode: string | null;
  reasonDetail: string;
}

@Component({
  selector: 'app-reject-reason-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    SelectModule,
    TextareaModule,
    ButtonModule,
    FloatLabelModule,
    MessageModule
  ],
  templateUrl: './reject-reason-dialog.html',
  styleUrl: './reject-reason-dialog.scss'
})
export class RejectReasonDialog {
  readonly visible = model<boolean>(false);
  readonly entreeName = input<string>('');
  readonly submitting = input<boolean>(false);

  readonly reject = output<{ reasonCode: string; reasonDetail?: string }>();
  readonly cancel = output<void>();

  private readonly menuItems = inject(MenuItemsService);

  readonly reasons = signal<RejectionReason[]>([]);
  readonly loadingReasons = signal<boolean>(false);

  private readonly rejectFormModel = signal<RejectReasonFormModel>({
    reasonCode: null,
    reasonDetail: ''
  });

  protected readonly rejectForm = form(this.rejectFormModel, (form) => {
    required(form.reasonCode);
    validate(form.reasonDetail, ({ value, valueOf }) => {
      const reasonCode = valueOf(form.reasonCode);
      if (reasonCode === 'Other' && !value()?.trim()) {
        return { kind: 'required', message: 'Please describe the reason' };
      }
      return null;
    });
  });

  readonly selectedReasonCode = computed(() => this.rejectForm().value().reasonCode);

  readonly selectedReason = computed(() => {
    const code = this.selectedReasonCode();
    return code ? (this.reasons().find((r) => r.code === code) ?? null) : null;
  });

  readonly isOtherSelected = computed(() => this.selectedReasonCode() === 'Other');

  constructor() {
    effect(() => {
      if (this.visible()) {
        untracked(() => {
          this.resetForm();
          void this.loadReasons();
        });
      }
    });
  }

  submit(): void {
    if (!this.rejectForm().valid()) {
      return;
    }

    const value = this.rejectForm().value();
    const reasonCode = value.reasonCode;
    const reasonDetail = value.reasonDetail;
    if (!reasonCode) {
      return;
    }

    this.reject.emit({
      reasonCode,
      reasonDetail: reasonDetail?.trim() ? reasonDetail.trim() : undefined
    });
  }

  onCancel(): void {
    this.cancel.emit();
    this.visible.set(false);
  }

  onVisibleChange(value: boolean): void {
    if (!value) {
      this.cancel.emit();
    }
    this.visible.set(value);
  }

  private resetForm(): void {
    this.rejectFormModel.set({ reasonCode: null, reasonDetail: '' });
    this.rejectForm().reset();
  }

  private async loadReasons(): Promise<void> {
    this.loadingReasons.set(true);

    try {
      const list = await firstValueFrom(this.menuItems.getRejectionReasons());
      this.reasons.set(list);
    } catch {
      // swallow; loadingReasons cleared in finally
    } finally {
      this.loadingReasons.set(false);
    }
  }
}
